<template>
  <div class="talk">
    <div class="talk-main" :style="{ display: isPreviewFull ? 'none' : '' }">
      <div class="talk-list" ref="talkContainer">
        <div v-for="(item, index) in filterTalkList" 
          :class="['talk-item', item.role === 'user' ? 'talk-item-right' : 'talk-item-left']"
          :key="'talk-' + index">
          <div class="talk-time">{{ formatTimestamp(item.ctime) }}</div>
          <img class="avatar" :src="cottonBotImg" />
          <div class="content">
            {{ item.message }}
          </div>
          <div class="option-list" v-if="item.role === 'user'">
            <el-button link title="复制" @click="copyMessage(item.message)"><i class="WSiconfont WSicon-fuzhitubiao" /></el-button>
            <el-button link title="引用" @click="toQuote(item.message)"><i class="WSiconfont WSicon-yinyong" /></el-button>
          </div>
          <template v-if="item.responseList">
            <div v-for="list in item.responseList" class="response-list" :key="list.agentId">
              <img class="avatar" :src="list.imgUrl || cottonBotImg" />
              <div class="bot-info">{{ list.aiName || 'cottonBot' }}</div>
              <div :class="['response-content', ['fileGenerate'].includes(list.type) ? 'no-width-content' : '']">
                <v-md-editor
                  v-if="list.type !== 'fileGenerate'"
                  v-model="list.content"
                  mode="preview"
                  :preview-options="previewOptions">
                </v-md-editor>
                <template v-else-if="list.type === 'fileGenerate' && getShowConfig(list).data">
                  <dataGenerate :config="getShowConfig(list)" @filePreview="toFilePreview(getShowConfig(list))" />
                </template>
              </div>
              <div class="option-list">
                <el-button link title="复制" @click="copyMessage(list.content)"><i class="WSiconfont WSicon-fuzhitubiao" /></el-button>
                <el-button link title="引用" @click="toQuote(list.content)"><i class="WSiconfont WSicon-yinyong" /></el-button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="talk-input">
        <el-input
          v-model="inputText"
          :autosize="{ minRows: 2, maxRows: 2 }"
          ref="inputRef"
          type="textarea"
          placeholder="可以问我任何问题哦~"
          @keydown.enter="toQuestion(true, $event)"
          @keydown="handleKeydown"
          @input="handleInput"
        />
        <!-- @ 候选浮层 -->
        <div
          v-if="showMentionList && filteredAgents.length > 1"
          class="mention-popover"
        >
          <div
            v-for="(agent, index) in filteredAgents"
            :key="agent.id"
            :class="['mention-item', { active: index === activeIndex }]"
            @click="selectAgent(agent)"
          >
            <img class="mention-item-avatar" :src="agent.imgUrl" />
            {{ agent.name }}
          </div>
        </div>
        <div class="quote-message" v-if="quoteMessage">
          <span>{{ quoteMessage }}</span>
          <el-icon  @click="quoteMessage = ''"><CircleCloseFilled /></el-icon>
        </div>
        <div class="input-bottom">
          <span>
            <el-button size="small" @click="clearInput">清空记录</el-button>
            <el-button size="small" @click="clearHistory">清空历史记录</el-button>
          </span>
          <span :title="inputText ? '发送' : ''" :class="['btn-submit', inputText ? 'btn-submit-high' : '']" @click="toQuestion(false, $event)">
            <el-icon><Top /></el-icon>
          </span>
        </div>
      </div>
    </div>
    <resultPreview ref="resultPreviewRef" @zoom="resultPreviewZoom" />
  </div>
</template>

<script setup>
  import { ref, defineProps, defineEmits, reactive, computed, onBeforeUnmount, nextTick, onMounted } from 'vue'
  import { cloneDeep } from 'lodash'
  import { crewbotworkflow, getcrewbotworkflow } from '@/port/clientSide/cottonBot.js'

  import dataGenerate from '@/components/common/chat/common/dataGenerate.vue';
  import resultPreview from '@/components/common/chat/common/resultPreview.vue';
  
  import VMdEditor from '@kangc/v-md-editor';
  import '@kangc/v-md-editor/lib/style/base-editor.css';
  import vuepressTheme from '@kangc/v-md-editor/lib/theme/vuepress.js';
  import '@kangc/v-md-editor/lib/theme/style/vuepress.css';
  // Prism
  import Prism from 'prismjs';
  // highlight code
  import 'prismjs/components/prism-json';

  VMdEditor.use(vuepressTheme, {
    Prism,
  });

  const props = defineProps({
    aiBotList: {
      type: Array,
      default: () => { return [] }
    },
    cottonBotId: {
      type: Number,
      default: 0
    },
    currentBot: {
      type: Object,
      default: () => { return {} }
    }
  })

  const emit = defineEmits(['changeGetData'])
  const cottonBotImg = 'https://bigdata.topeti.com/PCRepository/mainPlatform/cottonbotLogo.png'
  // md格式相关渲染
  const previewOptions = reactive({
    linkify: true,
    breaks: true,
    html: true,
    typographer: true
  })
  const talkContainer = ref(null)

  // 获取显示节点配置
  const getShowConfig = (item) => {
    const { type } = item
    let obj = {
      type,
      role: 'assistant',
      loading: false,
      content: ''
    }
    try {
      const content = item.content
      const jsonMatch = content.match(/{[\s\S]*}/)
      // ✅ 常见文件扩展名（可按需增减）
      const fileExtReg = /\.(csv|txt|json|xls|xlsx|doc|docx|pdf|png|jpg|jpeg|gif|bmp|zip|rar|7z|tar|gz)$/i;
      let res
      if (jsonMatch) {
        const cleanJson = jsonMatch[0]
        res = JSON.parse(cleanJson)
      } else if (fileExtReg.test(content)) {
        res = { data: content }
      }
      const data = res.data
      if (type === 'fileGenerate') {
        obj.data = data
        obj.name = getFileName(data)
      }
    } catch (error) {
      obj.content = '返回数据格式错误'
      console.log(error)
    }
    return obj
  }

  // 获取文件名字
  const getFileName = (name) => {
    const nameArr = name.split('-')
    if (nameArr.length > 1) {
      nameArr.splice(0, 1)
      name = nameArr.join('-')
    }
    return name
  }
  // 文件推送的预览
  const resultPreviewRef = ref(null)
  const isPreviewFull = ref(false)
  const toFilePreview = (val) => {
    const obj = {
      openType: val.type,
      fileUrl: '/knowledge/' + val.data,
      title: getFileName(val.data)
    }
    resultPreviewRef?.value.open(obj)
  }
  // 预览区域的缩放
   const resultPreviewZoom = (isFull) => {
    isPreviewFull.value = isFull
  }

  const inputText = ref('')
  const inputRef = ref(null)

  // @功能相关
  const showMentionList = ref(false)
  const mentionKeyword = ref('')
  const activeIndex = ref(0) // 键盘上下选中索引

  // 计算属性：过滤智能体
  const filteredAgents = computed(() => {
    let list = [ { name: '全部智能体', imgUrl: 'https://bigdata.topeti.com/PCRepository/mainPlatform/chat/s-enterprise-4.png' } ]
    if (!mentionKeyword.value) {
      list.push(...props.aiBotList)
    } else {
      list.push(...props.aiBotList.filter(a => a.appname.includes(mentionKeyword.value)))
    }
    return list 
  })

  /** 设置光标位置 */
  const setCursor = (pos) => {
    nextTick(() => {
      const ta = inputRef.value?.textarea
      if (ta) {
        ta.focus()
        ta.setSelectionRange(pos, pos)
      }
    })
  }

  /**
   * 解析所有 @xxx 片段
   * 返回 [{ start, end, name }]
   */
  const parseMentions = (text) => {
    const regex = /@([\u4e00-\u9fa5\w]+)/g
    const result = []
    let match

    while ((match = regex.exec(text)) !== null) {
      result.push({
        start: match.index,
        end: match.index + match[0].length,
        name: match[1],
      })
    }
    return result
  }


  // 1. 输入监听：检测 @
  const handleInput = () => {
    const textarea = inputRef.value?.textarea
    if (!textarea) return
    
    const cursor = textarea.selectionStart // ✅ 使用实时光标位置
    const textBeforeCursor = inputText.value.substring(0, cursor)
    const match = textBeforeCursor.match(/@([\u4e00-\u9fa5\w]*)$/)

    if (match) {
      const keyword = match[1]
      // 完整智能体名不弹窗
      const isExactAgent = props.aiBotList.some(agent => agent.appname === keyword)
      if (isExactAgent) {
        showMentionList.value = false
        return
      }
      // 非完整名才显示候选
      mentionKeyword.value = keyword
      showMentionList.value = true
      activeIndex.value = 0
      return
    }
    showMentionList.value = false
  }

  // 2. 键盘监听：上下选择 & 回车确认
  const handleKeydown = (e) => {
    const val = inputText.value
    const textarea = inputRef.value?.textarea
    if (!textarea) return
    
    const cursor = textarea.selectionStart
    const mentions = parseMentions(val)

    // ===== 1. Backspace 整体删除 @xxx =====
    if (e.key === 'Backspace') {
      for (const fragment of mentions) {
        // 场景A：光标刚好在@xxx的末尾
        if (cursor === fragment.end) {
          e.preventDefault()
          inputText.value = val.slice(0, fragment.start) + val.slice(fragment.end)
          setCursor(fragment.start)
          showMentionList.value = false
          return
        }

        // 场景B：光标在@xxx后面的空格右侧
        if (
          cursor === fragment.end + 1 &&
          val[fragment.end] === ' '
        ) {
          e.preventDefault()
          inputText.value = val.slice(0, fragment.start) + val.slice(cursor)
          setCursor(fragment.start)
          showMentionList.value = false
          return
        }
      }
    }

    // ===== 2. @列表显示时的键盘操作 =====
    if (showMentionList.value) {
      // 上下键选择
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        activeIndex.value = (activeIndex.value - 1 + filteredAgents.value.length) % filteredAgents.value.length
        return
      }
      
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        activeIndex.value = (activeIndex.value + 1) % filteredAgents.value.length
        return
      }

      // 回车/Tab 确认选择
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (filteredAgents.value[activeIndex.value]) {
          selectAgent(filteredAgents.value[activeIndex.value])
        }
        return
      }

      // ESC 关闭列表
      if (e.key === 'Escape') {
        showMentionList.value = false
        return
      }
    }
  }

  // 3. 选中智能体后的逻辑 (核心算法)
  const selectAgent = (agent) => {
    const textarea = inputRef.value?.textarea
    if (!textarea) return

    const curVal = inputText.value
    // ✅ 关键修复：使用当前实时光标位置，而不是旧的 cursorPos
    const cursor = textarea.selectionStart

    // 找到 @ 开始的位置（从光标向前找最近的 @）
    const textBeforeCursor = curVal.substring(0, cursor)
    const atIndex = textBeforeCursor.lastIndexOf('@')

    if (atIndex !== -1) {
      // 拆分字符串：前半部分 + @智能体名 + 后半部分
      const before = curVal.substring(0, atIndex)
      const after = curVal.substring(cursor)
      
      // 拼接新字符串 (注意后面加个空格，方便继续输入)
      const agentName = agent.appname || agent.name // 根据你的数据结构使用正确的字段
      const newValue = `${before}@${agentName} ${after}`
      inputText.value = newValue

      // 重新设置光标位置 (非常重要!)
      // 光标应该落在 @xxx 后面的空格之后
      const newCursorPos = atIndex + agentName.length + 2 // @ + name + space
      
      nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(newCursorPos, newCursorPos)
      })
    }

    showMentionList.value = false
    mentionKeyword.value = ''
  }

  const talkList = ref([])

  const filterTalkList = computed(() => {
    const currentBot = props.currentBot
    let list = []
    if (currentBot?.aiModelId) {
      talkList.value.forEach((iten, index) => {
        const item = cloneDeep(iten)
        if (item.role === 'result' && item.workRobot?.includes(currentBot.aiModelId)) {
          // 问题也要加进去
          list.push(talkList.value[index - 1])
          item.responseList = item.responseList.filter(item => {
            return item.agentId.search(currentBot.aiModelId) !== -1
          })
          list.push(item)
        }
      })
    } else {
      list = talkList.value
    }
    return list
  })
  import util from '@/api/util'
  const apiurls = util.apiUrl()
  const userToken = JSON.parse(localStorage.getItem('user')).accesstoken
  const schoolId = localStorage.getItem('userMsg') ? JSON.parse(localStorage.getItem('userMsg'))?.schoolMsg?.schoolId : null

  // 获取和问题匹配的智能体
  import { aiSearchArray } from '@/components/common/chat/common/utils.js'
  const getMatchBot = async (message) => {
    
    const botList = getBotListMessage.value.map(item => {
      return {
        id: item.aiModelId,
        tip: (item.message || item.name) + (item.isAloneTalk ? (item.aloneTalkContent || '') : '')
      }
    })
    const resBot = await aiSearchArray(botList, message)
    return getBotListMessage.value.filter(item => {
      const index = resBot.findIndex(iten => { return item.aiModelId === iten.id })
      return index !== -1
    })
  }

  const getRandomNum = () => {
    let randomNum = ''
    for (let i = 0; i < 6; i++) {
      randomNum += Math.floor(Math.random() * 10);
    }
    return Number(randomNum)
  }
  const toQuestion = async (isEnter = false, e) => {
    e.preventDefault()
    if ((!inputText.value && inputText.value !== 0) || (isEnter && showMentionList.value)) return false
    // 关闭以下@的弹窗
    showMentionList.value = false
    let message = inputText.value
    emit('cancelSelectedBot')
    clearInput()
    // 先添加一下输入问题的节点
    talkList.value.push({
      role: 'user',
      message: message,
      quoteMessage: quoteMessage.value,
      ctime: Date.now(),
      id: getRandomNum()
    })
    scrollBottom()

    setTimeout(() => {}, 100)
    // 添加返回节点
    const obj = {
      role: 'result',
      message: '已为你创建工作流，正在执行中',
      status: 'processing',
      id: getRandomNum()
    }
    talkList.value.push(obj)

    // 如果有引用的话就把引用的加上去
    if (quoteMessage.value) {
      message = quoteMessage.value + message
      quoteMessage.value = ''
    }
    
    // 如果存在@那就不需要去解析问题了
    const mentionedAgents = getMentionedAgents(message)
    const botListMsg = mentionedAgents.length > 0 ? mentionedAgents : await getMatchBot(message)
    const workRobotId = botListMsg.map(item => { return item.aiModelId })
    obj.workRobot = workRobotId
    const agents = []
    let mode = 0
    console.log('执行的智能体', botListMsg)
    // 如果是@的智能体要使用mode 2加个智能体去分配一下
    if (mentionedAgents.length > 0) {
      const preAgents = {
        id: '1', 
        name: '分配ai', // ai小易的名称
        instructions: '根据问题中@指定的智能体名称，让每个智能体各自执行一下自己的任务',
        tools: []
      }
      agents.push(preAgents)
    }
    for(const item of botListMsg) {
      const { valueKnowledge } = item.config
      const type = getAiType(item.config)
      const obj = {
        id: String(item.aiModelId), 
        name: item.name, // ai小易的名称
        instructions: (item.message || item.name) + (item.isAloneTalk ? (item.aloneTalkContent || '') : ''),
        tools: []
      }
      if (type === 'fileGenerate') {
        obj.instructions += '。你回答的内容只需要返回接口的数据，不要包含其他语句和语气词，也不要输出任何额外的解释文字'
        const query = {
          name: 'getquestionfile', // 接口名称
          description: '文件推送的接口，获取知识库文件', // 接口描述
          parameters: {
            type: 'object',
            properties: {
              question: {
                type: 'string',
                description: '提问词'
              },
              knowledgeBaseIds: {
                type: 'string',
                description: '知识库id'
              },
              schoolId: {
                type: 'number',
                description: '学校id'
              },
            }
          },
          api: {
            url: apiurls + `/aikl/getquestionfile?question=${item.message||message}&knowledgeBaseIds=${valueKnowledge.join(',')}&schoolId=${schoolId}`, // 接口地址
            method: 'GET',
            headers: {
              accesstoken: userToken, // token
            }
          }
        }
        obj.tools.push(query)
      } else {
        // 添加知识库信息
        if (valueKnowledge.length > 0) {
         obj.instructions = await getKnowledge(valueKnowledge, obj.instructions)
        }
      }
      agents.push(obj)
    }
    const query = {
      mode,
      userMessage: message,
      history: [],
      agents
    }
    emit('changeGetData', true, botListMsg)
    
    const { code, data: taskId } = await crewbotworkflow(query)
    if (code === 0) {
      obj.taskId = taskId
      
      await saveHistory()
      await getWorkFlowStatus(taskId)
      // 回答结束后也要到最底部
      scrollBottom()
      emit('changeGetData', false)
    }
  }
  
  // 滚动到对话最底部
  const scrollBottom = () => {
    // 等待 DOM 更新后滚动（关键！）
    nextTick(() => {
      const container = talkContainer.value;
      container.scrollTop = container.scrollHeight;
    });
  }

  const getMentionedAgents = (message) => {
    // 1. 找出所有 @xxx
    const mentionRegex = /@([\u4e00-\u9fa5\w]+)/g
    const mentionNames = []
    let match
    while ((match = mentionRegex.exec(message)) !== null) {
      mentionNames.push(match[1]) // match[1] 就是 @ 后面的名字
    }

    // 2. 去重（防止用户 @ 了两次同一个）
    const uniqueNames = [...new Set(mentionNames)]

    // 3. 把名字转换成 agent 对象（拿 ID）
    let mentionedAgents = []
    if (uniqueNames.includes('全部智能体')) {
      mentionedAgents = getBotListMessage.value
    } else {
      mentionedAgents = getBotListMessage.value.filter(agent => 
        uniqueNames.includes(agent.appname)
      )
    }
    return mentionedAgents
  }

  import { getquestionknowledge } from '@/port/admins/ai'
  const getKnowledge = async (valueKnowledge, val) => {
    let sendMessage = val
    let userMsg = localStorage.getItem("userMsg")
    let schoolId = null
    let konwledgeVal = ''
    if (userMsg) {
      schoolId = JSON.parse(userMsg).schoolMsg.schoolId
    }
    for (let i = 0; i < valueKnowledge.length; i++) {
      const query = {
        question: sendMessage,
        schoolId: schoolId,
        knowledgeBaseId: valueKnowledge[i],
      }
      const { code, data } = await getquestionknowledge(query)
      if (code === 0 && data !== '') {
        konwledgeVal += `<div style='display: none;'> ${data}</div>`
      }
    }
    sendMessage += konwledgeVal
    return sendMessage
  }

  // 获取任务流的状态
  const getWorkFlowStatus = async (taskId) => {
    const index = talkList.value.findIndex(item => { return item.taskId === taskId })
    const currentTask = talkList.value[index]
    while (!isDestroy.value && currentTask.status === 'processing') {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const { code, data } = await getcrewbotworkflow({ workflowId: taskId })
      if (code === 0) {
        if (data.response) {
          data.status = 'success'
          currentTask.status = data.status
          currentTask.message = '任务已完成'
          currentTask.ctime = Date.now()
          const response = data.response?.agentResponses || []
          currentTask.responseList = response.map(item => {
            const id = item.agentId.split('_')[1]
            const iten = getBotListMessage.value.find(iter => { return iter.aiModelId === Number(id) })
            if (iten) {
              item.aiName = iten.name
              item.aiConfig = iten
              item.type = getAiType(iten.config)
              item.imgUrl = iten.imgUrl
            }
            return item
          })
          talkList.value[index] = currentTask
          await saveHistory()
        }
      } else {
        currentTask.status === 'error'
      }
    }
  }
  // 获取ai小易节点的类型
  const getAiType = (config) => {
    const stepList = config.stepList || []
    const { type } = stepList[stepList.length - 1]
    return type
  }
  // 设置提示词
  const getBotListMessage = computed(() => {
    return props.aiBotList.map(item => {
      const val = cloneDeep(item)
      const config = JSON.parse(item.config)
      val.message = getSystemMessage(config.bindinf, config.customData, config.functionParms)
      val.config = config
      return val
    })
  })

  import { formatDate } from "@/api/global.js";
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return formatDate(date, "yyyy-MM-dd hh:mm");
  }

  // 历史记录部分
  import {
    getaioperatelist,
    addaioperate,
    updateaioperate
  } from '@/port/admins/ai.js'

  const historyId = ref(null)
  const saveHistory = async (clear = false) => {
    if (talkList.value.length > 0 || clear) {
      const fn = historyId.value ? updateaioperate : addaioperate
      const query = {
        bigcontent: JSON.stringify(talkList.value),
      }
      if (historyId.value) {
        query.id = historyId.value
      } else {
        query.content = '历史记录'
        query.cotId = props.cottonBotId
      }
      const { code } = await fn(query)
      if (code === 0) {
        if (!historyId.value) {
          // 如果是新增就获取一下历史记录
          getHistory()
        }
      }
    }
  }

  // 获取历史记录
  const getHistory = async (isInit) => {
    if (props.cottonBotId) {
      const query = {
        curpage: 1,
        pagesize: 50,
        cotId: props.cottonBotId
      }
      const { code, data } = await getaioperatelist(query)
      if (code === 0 && data.length > 0) {
        const { bigcontent, id } = data[0]
        talkList.value = JSON.parse(bigcontent)
        if (isInit) {
          const workingMission = talkList.value.filter(item => { return item.status === 'processing' })
          let workRobotId = []
          for (const item of workingMission) {
            if (item.workRobot?.length > 0) {
              workRobotId.push(...item.workRobot)
              getWorkFlowStatus(item.taskId).then(() => {
                emit('changeGetData', false, item.workRobot)
              })
            }
          }
          workRobotId = Array.from(new Set(workRobotId))
          emit('changeGetData', true, workRobotId)
        }
        
        historyId.value = id
      }
    }
  }

  import { dealText } from '@/api/common.js'
  import store from '@/store'
  // 获取预设提示词
  const getSystemMessage = (text, customData, functionParms) => {
    const _html = dealText({
      text: text,
      commonData: store.state.userData,
      customData: customData,
      functionParms: functionParms
    });
    
    // 过滤预设参数中的HTML标签
    const filteredBindinf = filterHtmlTags(_html);
    return filteredBindinf;
  }
  // 过滤HTML标签的方法
  const filterHtmlTags = (str) => {
    if (!str) return '';
    // 移除所有HTML标签
    return str.replace(/<[^>]+>/g, '')
    // 替换HTML实体
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    // 移除多余空格
    .replace(/\s+/g, ' ')
    .trim();
  }

  import { ElMessage } from 'element-plus';
  const copyMessage = async (text) => {
    const filteredText = filterHtmlTags(text)
    try {
      // 优先使用 Clipboard API（现代浏览器）
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(filteredText)
      } else {
        // 降级方案（兼容旧浏览器）
        const textarea = document.createElement('textarea')
        textarea.value = filteredText
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      ElMessage.success('复制成功')
    } catch (err) {
      console.error('复制失败:', err)
      ElMessage.error('复制失败')
    }
  }

  const quoteMessage = ref('')
  const toQuote = (text) => {
    quoteMessage.value = filterHtmlTags(text)
  }

  const clearInput = () => {
    inputText.value = ''
  }
  const clearHistory = () => {
    talkList.value = []
    saveHistory(true)
  }
  const isDestroy = ref(false)
  onBeforeUnmount(() => {
    isDestroy.value = true
  })
  onMounted(() => {
    getHistory(true)
  })
</script>

<style lang="scss" scoped>
  .talk {
    width: 100%;
    height: 100%;
    display: flex;
    .talk-main {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      height: 100%;
      flex: 1;
      overflow-y: auto;
    }
    .talk-list {
      width: 100%;
      flex: 1;
      overflow-y: auto;
      .talk-item {
        width: 100%;
        display: flex;
        box-sizing: border-box;
        margin: 10px 0px;
        padding: 0 20px;
        &-right {
          flex-direction: row-reverse;
        }
        &-left, &-right {
          flex-wrap: wrap;
        }
        .avatar {
          width: 35px;
          height: 35px;
          border-radius: 50%;
          border: 1px solid #4E86FF;
        }
        .content {
          max-width: 60%;
          padding: 10px;
          margin: 0 10px;
          background-color: #DAE3FF;
          box-shadow: 0px 0px 47.6px 0px rgba(172, 198, 255, 0.2);
          border-radius: 12px;
        }
        .talk-time {
          width: 100%;
          text-align: center;
          font-size: 12px;
          color: #999;
        }
        .option-list {
          width: 100%;
          text-align: right;
          margin: 10px 0;
          i {
            font-size: 20px;
          }
        }
        .response-list {
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          box-sizing: border-box;
          margin-top: 10px;
          .bot-info {
            width: 60%;
            display: flex;
            padding-left: 10px;
            align-items: center;
          }
          .response-content {
            width: 60%;
            margin-left: 47px;
            :deep(.vuepress-markdown-body) {
              padding: 0;
            }
          }
          .no-width-content {
            width: auto;
          }
          .option-list {
            text-align: left;
            margin-left: 40px;
          }
          
        }
      }
      
    }
    .talk-input {
      width: 60%;
      max-width: 960px;
      min-height: 114px;
      margin: 20px 0;
      border: 1px solid #E7EAF0;
      border-radius: 24px;
      padding: 12px;
      position: relative;
      box-sizing: border-box;
      :deep(.el-textarea__inner) {
        box-shadow: none;
      }
      .quote-message {
        height: 20px;
        line-height: 20px;
        span {
          display: inline-block;
          width: 95%;
          white-space: nowrap; /* 不换行 */
          overflow: hidden;    /* 溢出隐藏 */
          text-overflow: ellipsis; /* 省略号 */
        }
        i {
          cursor: pointer;
          position: relative;
          top: -5px;
        }
      }
      .input-bottom {
        display: flex;
        justify-content: space-between;
        align-items: end;
        :deep(.el-button) {
          border-radius: 27px;
        }
      }
      .btn-submit {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 35px;
        height: 35px;
        border-radius: 50%;
        flex-shrink: 0;
        background-color: #D6DEE8;
        cursor: not-allowed;
        i {
          font-size: 18px;
          color: #fff;
        }
      }
      .btn-submit-high {
        background-color: #4E86FF;
        cursor: pointer;
        &:hover {
          background-color: #66b1ff;
        }
      }
      .mention-popover {
        position: absolute;
        bottom: 100%;          /* 关键：贴在输入框上沿 */
        left: 0;
        width: 240px;
        max-height: 200px;
        overflow-y: auto;
        background: #fff;
        border: 1px solid #dcdfe6;
        border-radius: 6px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        margin-bottom: 6px;   /* 和输入框留一点间距 */
      }

      .mention-item {
        padding: 8px 12px;
        cursor: pointer;
        height: 50px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
      }
      .mention-item:hover,
      .mention-item.active {
        background-color: #ecf5ff;
        color: #409eff;
      }
      .mention-item-avatar {
        width: 30px;
        height: 30px;
        margin-right: 4px;
      }
    }
  }
</style>